import { useRef } from "react";
import { useTellescopeInfiniteQuery } from "@tellescope/tanstack-query";

/**
 * Verifies a Tellescope Product record already exists for each of the given Stripe price IDs, via
 * reverse lookup. The shop does not create Product records — customers configure them in the
 * legacy web app first, keyed by stripePriceId. Returns a map so callers can look up per-price
 * resolution without running one query per selected row.
 *
 * Every change to stripePriceIds (adding/removing a selected price) changes the query's filter, so
 * TanStack Query treats it as a brand-new key with no cache — data goes back to undefined and
 * isFetched back to false until the new fetch settles, even for price IDs whose resolution was
 * already known from a previous fetch. Answering purely from the live query result would make an
 * already-resolved (or already-confirmed-unresolved) row flicker each time a sibling price is added
 * or removed. knownResultsRef accumulates resolved/unresolved answers per price ID across those
 * query-key changes, so a row's answer — once known — never regresses just because the selection
 * changed elsewhere; only genuinely new, never-before-seen price IDs report as unchecked.
 */
export const useResolveTellescopeProducts = (stripePriceIds: string[]) => {
  const query = useTellescopeInfiniteQuery({
    model: "products",
    method: "getSome",
    request: { filter: stripePriceIds.length ? { stripePriceId: { _in: stripePriceIds } } : {} },
    enabled: stripePriceIds.length > 0,
    select: (data) => data.pages.flat(),
  });

  type ResolvedProduct = NonNullable<typeof query.data>[number];
  const knownResultsRef = useRef(new Map<string, ResolvedProduct | null>());

  // The completed fetch's own request is the authoritative "was this ID checked" set — every ID in
  // it that ISN'T in the response is confirmed unresolved (null), not merely "not yet known".
  if (query.isFetched && query.data) {
    const resolvedByPriceId = new Map<string, ResolvedProduct>();
    // getSome returns newest first; keeping the first match per price checks the same Product that
    // checkout charges (usePrepareShopCheckout) when several share a price.
    for (const product of query.data) {
      if (product.stripePriceId && !resolvedByPriceId.has(product.stripePriceId)) {
        resolvedByPriceId.set(product.stripePriceId, product);
      }
    }
    for (const stripePriceId of stripePriceIds) {
      knownResultsRef.current.set(stripePriceId, resolvedByPriceId.get(stripePriceId) ?? null);
    }
  }

  const productByStripePriceId = new Map<string, ResolvedProduct>();
  for (const [stripePriceId, result] of knownResultsRef.current) {
    if (result) productByStripePriceId.set(stripePriceId, result);
  }

  return {
    productByStripePriceId,
    isLoading: query.isLoading,
    error: query.error,
    /** True once every currently-selected price ID has a known (checked) answer, resolved or not. */
    hasChecked: stripePriceIds.every((id) => knownResultsRef.current.has(id)),
    /** Per-price variant of hasChecked — true once THIS SPECIFIC price ID has a known answer. */
    hasCheckedPriceId: (stripePriceId: string) => knownResultsRef.current.has(stripePriceId),
  };
};
